import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateMachineName } from '../../services/api';
import { useI18n } from '../../i18n/useI18n';

export interface MachineRenameControlProps {
  machineCode: string;
  machineName?: string | null;
  onRenamed?: (machineName: string) => void;
  /** Icon size for the edit button */
  size?: 'small' | 'medium';
}

const MachineRenameControl: React.FC<MachineRenameControlProps> = ({
  machineCode,
  machineName,
  onRenamed,
  size = 'small',
}) => {
  const { pages: t } = useI18n();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const code = String(machineCode || '').trim().toUpperCase();

  const openDialog = () => {
    setName(String(machineName || '').trim());
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
  };

  const save = async () => {
    const next = name.trim();
    if (!code || !next) {
      toast.error(t.deviceMgmt.namePlaceholder);
      return;
    }
    try {
      setSaving(true);
      const res = await updateMachineName(code, next);
      if (res.success) {
        const saved = res.machine?.machineName || next;
        onRenamed?.(saved);
        toast.success(t.deviceMgmt.renameSuccess);
        setOpen(false);
      }
    } catch (err: any) {
      console.error('Error renaming machine:', err);
      toast.error(err?.response?.data?.error || 'Không thể cập nhật tên máy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip title={t.deviceMgmt.renameTitle}>
        <IconButton size={size} onClick={openDialog} aria-label={t.deviceMgmt.renameTitle} sx={{ ml: 0.25 }}>
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t.deviceMgmt.renameTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t.deviceMgmt.machineCode} <strong>{code}</strong>
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label={t.deviceMgmt.nameLabel}
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.deviceMgmt.namePlaceholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !saving) {
                  e.preventDefault();
                  void save();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            {t.common.cancel}
          </Button>
          <Button onClick={() => void save()} variant="contained" disabled={saving || !name.trim()}>
            {saving ? t.common.updating : t.common.update}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MachineRenameControl;
